import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditCompaniesCustomerModal from '../../components/companies/EditCompaniesCustomerModal';
import PageHeader from '../../components/PageHeader';
import {
  useCreateCompaniesCustomerMutation,
  useDeleteCompaniesCustomerMutation,
  useGetCompaniesCustomerDataQuery,
  useGetCompaniesCustomerListQuery,
  useUpdateCompaniesCustomerMutation,
} from '../../store/apiSlices/companies/companiesCustomer/companiesCustomerApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';
// ------------------- Types -------------------
interface CompaniesCustomer {
  srno: number;
  shortName: string;
  customer: string;
  status: string;
  actions: string;
}

interface ActionButton {
  label: string;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type CustomerFormValues = {
  shortName: string;
  companyId: string;
  status: string;
};

const CompaniesCustomerContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddCompaniesCustomerModal, setShowAddCompaniesCustomerModal] =
    useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<CompaniesCustomer | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    defaultValues: {
      shortName: '',
      companyId: '',
      status: 'Active',
    },
  });

  // API calls
  const {
    data: companiesCustomerList,
    isLoading,
    refetch,
  } = useGetCompaniesCustomerListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [createCustomer] = useCreateCompaniesCustomerMutation();
  const [updateCustomer] = useUpdateCompaniesCustomerMutation();
  const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCompaniesCustomerMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<CompaniesCustomer>({
      deleteMutation: deleteCustomer,
      refetch,
      itemName: 'customer',
      idField: 'srno',
    });

  // Get companies customer data for the modal - only fetch when modal is open
  const { data: companiesCustomerData } = useGetCompaniesCustomerDataQuery(undefined, {
    skip: !showAddCompaniesCustomerModal,
    refetchOnMountOrArgChange: true,
  });

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('companies.addCustomer'),
          variant: 'blue',
          onClick: () => {
            reset({
              shortName: '',
              companyId: '',
              status: 'Active',
            });
            setShowAddCompaniesCustomerModal(true);
          },
        },
      ]
    : [];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: CompaniesCustomer) => {
      setEditingCustomer(rowData);
      reset({
        shortName: rowData.shortName,
        companyId: String(rowData?.customer ?? ''),
        status: rowData.status,
      });
      setShowAddCompaniesCustomerModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'customer',
        headerName: t('table.columns.company'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'shortName',
        headerName: t('table.columns.shortName'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.status} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('companies.customers'), isActive: true, onClick: () => {} },
  ];

  // Form submission handler
  const onSubmit = useCallback(
    async (values: CustomerFormValues) => {
      try {
        if (!editingCustomer) {
          const payload = {
            companyId: Number(values.companyId),
            shortName: String(values.shortName).trim(),
            active: String(values.status).toLowerCase() === 'active',
          };
          const res: any = await createCustomer(payload).unwrap();
          showSuccessToast(res?.message);
          refetch();
          setShowAddCompaniesCustomerModal(false);
          setEditingCustomer(null);
          reset({
            shortName: '',
            companyId: '',
            status: 'Active',
          });
        } else {
          const id = editingCustomer.srno; // Use the customer ID, not the company ID
          const payload = {
            shortName: String(values.shortName).trim(),
            active: String(values.status).toLowerCase() === 'active',
          };
          const res: any = await updateCustomer({ id, payload }).unwrap();
          showSuccessToast(res?.message);
          refetch();
          setShowAddCompaniesCustomerModal(false);
          setEditingCustomer(null);
          reset({
            shortName: '',
            companyId: '',
            status: 'Active',
          });
        }
      } catch (error) {
        logger.error('Failed to create customer', error as Error);
      }
    },
    [editingCustomer, createCustomer, updateCustomer, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddCompaniesCustomerModal(false);
    setEditingCustomer(null);
    reset({
      shortName: '',
      companyId: '',
      status: 'Active',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={companiesCustomerList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="companies-customer-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('companies.deleteCustomer')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddCompaniesCustomerModal && (
        <EditCompaniesCustomerModal
          data={{
            companiesCustomers: companiesCustomerData?.companiesCustomers ?? [],
            companies: companiesCustomerData?.companies ?? [],
            statusOptions: companiesCustomerData?.statusOptions ?? [
              { value: 'Active', label: t('common.active') },
              { value: 'Inactive', label: t('common.inactive') },
            ],
          }}
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingCustomer}
          isSubmitting={false}
        />
      )}
    </Stack>
  );
};

export default CompaniesCustomerContainer;
